import Phaser from "phaser";
import { FAMILY_BODY, FAMILY_HEADING, GAME_HEIGHT, GAME_WIDTH } from "../util/constants";
import { ensureBackdropTexture, BACKDROPS } from "../art/BackdropArt";
import { getMusic, MUSIC } from "../audio/Music";
import { drawPanel } from "../ui/Panel";
import { Button } from "../ui/Button";
import { BATTLES } from "../data/battles";
import { getSevenPath, loadSave, writeSave } from "../util/save";
import { reconcileUnlocks } from "../data/unlocks";
import { sfxClick } from "../audio/Sfx";
import { SettingsButton } from "../ui/SettingsButton";
import { createScrollableText } from "../ui/scrollableText";

// World map / battle log. Lists all 21 battle nodes; lit-up = playable & unlocked.
export class OverworldScene extends Phaser.Scene {
  constructor() { super("OverworldScene"); }

  create(): void {
    // Self-heal the unlock cache before drawing a single card. Unlocks
    // are derived from completed battles, so a save made before an
    // unlock-graph change can be missing entries it has plainly earned
    // — the reason Chapter 29 sat locked for players who had already
    // beaten Chapter 28 when the epilogue was wired up. Only ever adds.
    {
      const raw = loadSave();
      const healed = reconcileUnlocks(raw, getSevenPath(raw));
      if (healed !== raw) writeSave(healed);
    }
    const bgKey = ensureBackdropTexture(this, "bg_overworld", BACKDROPS.thuling);
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    const v = this.add.graphics();
    v.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.6, 0.6, 0.85, 0.85);
    v.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, 50, "ANTHROS — Chronicle of Battles", {
      fontFamily: FAMILY_HEADING,
      fontSize: "32px",
      color: "#f4d999",
      stroke: "#1a0e04",
      strokeThickness: 4
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 92, "Pick a battle to enter. Lit nodes are playable in this build; the rest scaffold the full story.", {
      fontFamily: FAMILY_BODY,
      fontSize: "14px",
      color: "#c9b07a"
    }).setOrigin(0.5);

    // Layout: 4 rows × 8 columns of battle cards (32 slots, 30 used).
    // Bumped from 3 rows × 7 cols (21 total) when the chapter list
    // expanded to 30. With the prior layout the bottom two rows
    // overlapped the detail panel at y=510 and — since cards are
    // created AFTER the panel in this scene — rendered ON TOP of it,
    // hiding the hover-detail text behind a wall of card backgrounds.
    // The new geometry keeps the grid above y=470 with a 40px breath
    // before the detail panel begins.
    const startX = 60;
    const startY = 140;
    const cardW = 136;
    const cardH = 80;
    const gapX = 10;
    const gapY = 12;
    const cols = 8;

    const save = loadSave();

    // Dedupe BATTLES by index for the grid display. The 7 B19
    // path-opener variants (b19_path_opener_vengeance / restoration /
    // revolution / duty / exile / mercy / forgetting) all share
    // index=19 — showing all 7 as separate cards would (a) push the
    // grid to 5 rows and overlap the detail panel below, and (b)
    // confuse the player by showing 7 identical "Battle 19" cards
    // before they've even reached the path divergence at B18.
    // Picking the FIRST entry per index gives one canonical card
    // per chapter number; the actual path routing happens at click
    // time once Seven Paths gating ships in a future commit.
    // Path-variant battles share an index (all seven B19 openers are #19)
    // and collapse to ONE card. Before the Seven Paths choice, the first
    // variant stands in as a generic gated card; after the choice, the
    // CHOSEN path's variant takes the slot so hover copy, unlock state and
    // routing all reflect the player's actual campaign.
    const chosenPath = getSevenPath(save);
    const visibleBattles: typeof BATTLES = [];
    const seenIndexes = new Set<number>();
    for (const b of BATTLES) {
      if (seenIndexes.has(b.index)) continue;
      seenIndexes.add(b.index);
      const isPathVariant = b.id.includes("path_opener")
        || b.id.includes("path_climax")
        || b.id.includes("path_final");
      if (isPathVariant && chosenPath) {
        const chosen = BATTLES.find(
          (x) => x.index === b.index && x.id.endsWith(chosenPath)
        );
        visibleBattles.push(chosen ?? b);
      } else {
        visibleBattles.push(b);
      }
    }

    const detailG = this.add.graphics();
    const detailX = 40;
    const detailY = 510;
    const detailW = GAME_WIDTH - 80;
    const detailH = 160;
    drawPanel(detailG, detailX, detailY, detailW, detailH);

    // Button geometry computed up front so the body's wordWrap can use the
    // button's left edge as its right boundary. Previously body wrap was
    // (detailW - 220) which was based on panel width, not button position —
    // off by ~24px because the body text has its own 24px left margin and
    // the wrap width didn't account for the gap-to-button.
    const btnW = 180;
    const btnH = 40;
    const btnLeft = detailX + detailW - btnW - 24;          // 24px right padding
    const btnTop = detailY + detailH - btnH - 20;           // 20px bottom padding
    const bodyLeft = detailX + 24;
    const bodyWrapWidth = btnLeft - bodyLeft - 24;          // 24px gap before button

    const detailTitle = this.add.text(bodyLeft, detailY + 18, "", {
      fontFamily: FAMILY_HEADING,
      fontSize: "22px",
      color: "#f4d999"
    });
    const detailSub = this.add.text(bodyLeft, detailY + 50, "", {
      fontFamily: FAMILY_BODY,
      fontSize: "14px",
      color: "#c9b07a"
    });

    // Body uses the scrollable-text helper instead of the previous "truncate
    // to 3 lines + ellipsis" hack. Long intros stay readable in full via
    // the mouse wheel; the helper auto-shows a thin gold scrollbar on the
    // right edge of the body region when the body overflows the visible
    // height.
    //
    // Body extends to the BOTTOM of the panel (with 12px padding), not
    // just to the top of the button — earlier version tied bodyBottom to
    // btnTop-12 which collapsed the body to ~10px tall and clipped almost
    // the entire intro. The button doesn't actually block the body
    // vertically: bodyWrapWidth already excludes the button's horizontal
    // range, so body text + button can coexist at the same y range without
    // ever overlapping. The scrollbar lands ~30px to the left of the
    // button (sbX = bodyLeft + bodyWrapWidth - 6), well clear of it.
    const bodyTop = detailY + 72;
    const bodyBottom = detailY + detailH - 12;
    const bodyHeight = bodyBottom - bodyTop;
    const bodyHandle = createScrollableText(this, {
      x: bodyLeft,
      y: bodyTop,
      w: bodyWrapWidth,
      h: bodyHeight,
      text: "",
      style: {
        fontFamily: FAMILY_BODY,
        fontSize: "14px",
        color: "#dad3bd",
        lineSpacing: 4
      }
    });

    let selectedId: string | null = null;
    const playBtn = new Button(this, {
      x: btnLeft,
      y: btnTop,
      w: btnW, h: btnH,
      label: "Enter Battle ▸",
      primary: true,
      enabled: false,
      fontSize: 16,
      onClick: () => {
        if (!selectedId) return;
        sfxClick();
        const b = BATTLES.find((x) => x.id === selectedId);
        if (!b) return;
        // Defensive check: same gating as the cards. The button is
        // disabled on locked-card hover so this branch shouldn't fire,
        // but a future code path that enables the button should still
        // be blocked from routing into a locked battle.
        const isUnlocked = save.unlockedBattles.includes(b.id);
        if (!isUnlocked) {
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "Locked — complete previous battles to unlock.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#ff9c7a"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 1800, onComplete: () => t.destroy() });
          return;
        }
        // Completed battles are read-only — the campaign moves forward
        // only. Same defensive check as the click handlers below.
        if (save.completedBattles.includes(b.id)) {
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "Already complete — the campaign only moves forward.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#a4d36a"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 1800, onComplete: () => t.destroy() });
          return;
        }
        if (b.playable) {
          this.scene.start("BattlePrepScene", { battleId: b.id });
        } else {
          // Show "not yet playable" floater
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "This battle is scaffolded for the full story — not yet playable.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#ff9c7a"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 1800, onComplete: () => t.destroy() });
        }
      }
    });

    // Back to Camp — the world map is now reached FROM the camp via
    // its "Where to Next?" hotspot, so the natural back action is to
    // return to camp (which itself has a Title button for quitting).
    // Camp Hub commit 1.
    new Button(this, {
      x: 40, y: GAME_HEIGHT - 56,
      w: 140, h: 40,
      label: "◂ Camp",
      primary: false,
      fontSize: 14,
      onClick: () => this.scene.start("CampScene")
    });

    // Roster button — opens RosterScene as a paused overlay so the player
    // can review their party's current levels / stats / abilities between
    // battles. Sits next to the Camp button at the bottom-left.
    new Button(this, {
      x: 196, y: GAME_HEIGHT - 56,
      w: 140, h: 40,
      label: "Roster",
      primary: false,
      fontSize: 14,
      onClick: () => {
        sfxClick();
        this.scene.pause();
        this.scene.run("RosterScene", { from: this.scene.key });
      }
    });

    visibleBattles.forEach((b, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (cardW + gapX);
      const y = startY + row * (cardH + gapY);

      // Four-state cascade per battle:
      //   - LOCKED: not in save.unlockedBattles. Player hasn't reached this
      //     point in the story. Card visible (builds anticipation) but
      //     darker; hover hides intro to avoid spoilers; click is no-op
      //     with a floater explaining how to unlock.
      //   - SCAFFOLDED: unlocked but b.playable === false. Battle is in
      //     the data but no map/units authored yet. Hover shows intro +
      //     "Story Scaffold ▸" affordance; click shows the existing
      //     "scaffolded for the full story" floater.
      //   - PLAYABLE: unlocked AND b.playable AND not yet in
      //     completedBattles. Click enters the battle.
      //   - CLEARED: unlocked AND b.playable AND in completedBattles.
      //     The campaign is one-way — players can re-read the intro on
      //     hover but can't re-enter the fight. Visual: muted blue-grey
      //     with a green-tinted border + the existing ✓ checkmark, so
      //     it reads as "done well", not "blocked". Click → floater.
      const unlocked = save.unlockedBattles.includes(b.id);
      const completed = save.completedBattles.includes(b.id) && unlocked;
      const playable = b.playable && unlocked && !completed;
      const cleared = b.playable && unlocked && completed;
      const scaffolded = !b.playable && unlocked;
      const locked = !unlocked;

      const cardG = this.add.graphics();
      // Color tier:
      //   playable  → deep blue
      //   cleared   → muted blue-green (done; still readable)
      //   scaffolded → mid grey
      //   locked    → near-black
      const fillTop =
        playable ? 0x141a2a :
        cleared  ? 0x101a18 :
        scaffolded ? 0x0d0f17 :
                   0x07090f;
      const fillBot =
        playable ? 0x070912 :
        cleared  ? 0x070d0c :
        scaffolded ? 0x05060a :
                   0x030408;
      cardG.fillGradientStyle(fillTop, fillTop, fillBot, fillBot, 1);
      cardG.fillRect(x, y, cardW, cardH);
      const border =
        playable ? 0xc9b07a :
        cleared  ? 0x4a7a5a :
        scaffolded ? 0x4a4a52 :
                   0x2a2a32;
      cardG.lineStyle(1, border, 1);
      cardG.strokeRect(x + 0.5, y + 0.5, cardW - 1, cardH - 1);

      // index badge
      const badgeColor =
        playable ? "#f4d999" :
        cleared  ? "#7a9a82" :
        scaffolded ? "#6a6a72" :
                   "#3a3a42";
      const badge = this.add.text(x + 6, y + 4, `#${b.index}`, {
        fontFamily: FAMILY_HEADING,
        fontSize: "11px",
        color: badgeColor
      });
      // title — compact "Battle N" instead of "First/Second/..." so the
      // narrow card width doesn't wrap two-word titles into a second line
      // that would push the subtitle off the bottom of the card.
      const titleColor =
        playable ? "#dccfa8" :
        cleared  ? "#8aa092" :
        scaffolded ? "#76747a" :
                   "#46454a";
      const t1 = this.add.text(x + cardW / 2, y + 18, `Battle ${b.index}`, {
        fontFamily: FAMILY_HEADING,
        fontSize: "12px",
        color: titleColor
      }).setOrigin(0.5, 0);
      // subtitle: hidden on locked battles to avoid spoilers in the grid view
      const subColor =
        playable ? "#f8f0d8" :
        cleared  ? "#9ab0a2" :
        scaffolded ? "#9a9aa0" :
                   "#46454a";
      const subText = locked ? "— locked —" : b.subtitle;
      const t2 = this.add.text(x + cardW / 2, y + 35, subText, {
        fontFamily: FAMILY_BODY,
        fontSize: "12px",
        color: subColor,
        wordWrap: { width: cardW - 12 },
        align: "center",
        fontStyle: locked ? "italic" : "normal"
      }).setOrigin(0.5, 0);
      const diffColor =
        playable ? "#c9b07a" :
        cleared  ? "#6a8a72" :
        scaffolded ? "#5a5a62" :
                   "#3a3a42";
      const t3 = this.add.text(x + cardW / 2, y + cardH - 16, b.difficultyLabel, {
        fontFamily: FAMILY_BODY,
        fontSize: "10px",
        color: diffColor
      }).setOrigin(0.5, 0);

      if (completed) {
        const dot = this.add.text(x + cardW - 14, y + 2, "✓", {
          fontFamily: FAMILY_HEADING,
          fontSize: "14px",
          color: "#a4d36a"
        });
        void dot;
      }
      // (No corner lock glyph — the 🔒 emoji rendered as "??" in the canvas
      // font stack on some systems, and locked cards already read as locked
      // via the "— locked —" subtitle + darkened tier colors.)

      // Path-specific battles need different hover + click handling.
      // The deduped B19 card represents one of seven path-opener
      // variants; the actual battle to launch isn't decided until
      // B18's pick. B23/B24 climax + B28 final are path-flavored
      // similarly. For all of them, surface generic copy on hover
      // and a "pick a path first" message on click rather than
      // routing the player into the wrong variant's prep.
      // Gate path battles only while the Seven Paths choice is unmade —
      // once chosen, this card IS the chosen variant (see the dedupe swap
      // above) and flows through the normal locked/playable states.
      const isPathBattle = (b.id.includes("path_opener")
        || b.id.includes("path_climax")
        || b.id.includes("path_final")) && !chosenPath;

      // Hit area — locked / cleared cards are still interactive (hover
      // shows the right context, click surfaces an explanatory floater)
      // but only PLAYABLE cards route into BattlePrepScene.
      const zone = this.add.zone(x, y, cardW, cardH).setOrigin(0, 0).setInteractive({ useHandCursor: true });
      const hoverBorder =
        playable ? 0xffd97a :
        cleared  ? 0x6aa07a :
        scaffolded ? 0x8a8a92 :
                   0x4a4a52;
      zone.on("pointerover", () => {
        cardG.lineStyle(2, hoverBorder, 1);
        cardG.strokeRect(x + 0.5, y + 0.5, cardW - 1, cardH - 1);
        selectedId = b.id;
        if (locked) {
          detailTitle.setText(`Battle ${b.index} — Locked`);
          detailSub.setText("Complete previous battles to unlock");
          bodyHandle.setText("This battle hasn't been unlocked yet. Progress through the story to reach it.");
          playBtn.setEnabled(false);
          playBtn.setLabel("Locked");
        } else if (isPathBattle) {
          // Path-flavored chapter — the actual content depends on
          // which path the player picked at B18. Show generic copy
          // so we don't spoil any specific path's intro.
          detailTitle.setText(`Battle ${b.index} — Path-Specific`);
          detailSub.setText(`Resolves based on your Seven Paths choice at Battle 18`);
          bodyHandle.setText("This chapter's contents are determined by the path you choose at Battle 18 (Seven Names, One Choice). Visit the camp signpost when you reach the divergence point to pick your path; the chapter's specific shape will resolve from that decision.");
          playBtn.setEnabled(false);
          playBtn.setLabel("Pick path at B18");
        } else if (cleared) {
          // Already-completed battle — full intro stays visible so
          // the player can re-read what happened, but the play button
          // is disabled. The campaign moves forward only.
          detailTitle.setText(`${b.title} — ${b.subtitle}`);
          detailSub.setText(`Cleared · ${b.difficultyLabel}`);
          bodyHandle.setText(b.intro);
          playBtn.setEnabled(false);
          playBtn.setLabel("Cleared ✓");
        } else {
          detailTitle.setText(`${b.title} — ${b.subtitle}`);
          detailSub.setText(`${b.difficultyLabel} · music: ${b.music.replace("music_", "").replace(/_/g, " ")}`);
          // Full intro shown — scrollable when it overflows. setText resets
          // scroll to the top so the player sees the start of every new
          // hovered battle.
          bodyHandle.setText(b.intro);
          playBtn.setEnabled(true);
          playBtn.setLabel(playable ? "Enter Battle ▸" : "Story Scaffold ▸");
        }
      });
      zone.on("pointerout", () => {
        cardG.lineStyle(1, border, 1);
        cardG.strokeRect(x + 0.5, y + 0.5, cardW - 1, cardH - 1);
      });
      zone.on("pointerup", () => {
        if (isPathBattle) {
          // Show "pick a path first" floater instead of routing into
          // a specific variant's prep. Once Seven Paths gating ships
          // and the player has picked a path, this branch resolves
          // the path-specific battle id and routes appropriately.
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "Pick your path at Battle 18 first — this chapter resolves from that choice.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#ff9c7a",
            wordWrap: { width: GAME_WIDTH - 80 },
            align: "center"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 2400, onComplete: () => t.destroy() });
          return;
        }
        if (cleared) {
          // Already-completed — surface a positive-tone floater (green)
          // rather than an error tone (red), so the player understands
          // this is "you already did this" not "you can't do this".
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "Already complete — the campaign only moves forward.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#a4d36a"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 1800, onComplete: () => t.destroy() });
          return;
        }
        if (playable) {
          this.scene.start("BattlePrepScene", { battleId: b.id });
        } else if (locked) {
          // Floater: "Locked — complete previous battles to unlock"
          const t = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 90, "Locked — complete previous battles to unlock.", {
            fontFamily: FAMILY_BODY,
            fontSize: "16px",
            color: "#ff9c7a"
          }).setOrigin(0.5);
          this.tweens.add({ targets: t, alpha: 0, duration: 1800, onComplete: () => t.destroy() });
        }
        // Scaffolded (unlocked but unauthored) is handled by the playBtn
        // onClick — the floater there is the same "story scaffold" message.
      });

      void badge; void t1; void t2; void t3;
    });

    getMusic(this).play(MUSIC.adventureAnthros, { fadeMs: 800 });
    this.cameras.main.fadeIn(450, 0, 0, 0);

    new SettingsButton(this, GAME_WIDTH - 32, 32);
  }
}
